import { gql } from '@apollo/client'

// Notification settings hub (Configuration › Notifications), Maestro schema only.
// Contract: docs/superpowers/plans/2026-10-03-notification-settings-graphql-contract.md (maestro-api).
//
// Never select these fields from a shared document (GET_APP_INIT_QUERY, CONFIGURATION_HUB_COUNTS_QUERY):
// a site whose backend runs an older gem branch rejects the whole document. Every query here is sent
// only when `appStore.enableNotificationSettings` is on, and the first one (capabilities) is the probe.
// Selections stay inline (no fragments): Apollo runs with addTypename disabled in this app.

const SETTING_FIELDS = `
	id reminderType name description audience topic scheduleKind scheduleSummary channels
	systemEnabled lockedBySystem critical recipientCount
	replacedByWorkflow { cadenceId name }
`

const SETTING_DETAIL_FIELDS = `
	${SETTING_FIELDS}
	schedule availableChannels content recommended lastEditedBy lastEditedAt
	recipientCountByChannel onlyChannelCounts hasOriginal nextSendAt companyTimezone
	variables { key label description sample }
	linked { reminderType name }
`

const IMPACT_FIELDS = `
	level affectedTotal affectedByChannel onlyChannelLoss linkedNotifications
	nextSendBefore nextSendAfter frequencyMultiplier changes messages requiresReason
`

export const NOTIFICATION_CAPABILITIES_QUERY = gql`
	query notificationCapabilities {
		notificationCapabilities {
			read
			edit
			enabled
		}
	}
`

// Amendment A16: how many automated messages Maestro support has turned on for the hub ("Not set up yet" at 0, and no
// list tour). Its own document, never merged into the capabilities probe: an API without the field rejects only this
// one (sent with silentErrors) and the page counts the list instead.
export const NOTIFICATION_VISIBLE_COUNT_QUERY = gql`
	query notificationVisibleCount {
		notificationCapabilities {
			visibleCount
		}
	}
`

// Configuration hub card meta only ("20 on · 2 off"). Its own document, never merged into the hub counts query.
export const NOTIFICATION_HUB_COUNTS_QUERY = gql`
	query notificationHubCounts {
		notificationSettings {
			id
			systemEnabled
			lockedBySystem
		}
	}
`

export const NOTIFICATION_SETTINGS_QUERY = gql`
	query notificationSettings($audience: String, $topic: String, $search: String) {
		notificationSettings(audience: $audience, topic: $topic, search: $search) { ${SETTING_FIELDS} }
	}
`

export const NOTIFICATION_SETTING_QUERY = gql`
	query notificationSetting($id: ID!) {
		notificationSetting(id: $id) { ${SETTING_DETAIL_FIELDS} }
	}
`

// The channels "Restore default message" can restore (resettableChannels, B4). Its own document, never part of the
// detail query or the save answers: an API without the field rejects only this one ("doesn't exist", not toasted) and
// the editor falls back to `hasOriginal`. Asked again after every save, reset or restore.
export const NOTIFICATION_RESETTABLE_CHANNELS_QUERY = gql`
	query notificationResettableChannels($id: ID!) {
		notificationSetting(id: $id) {
			id
			resettableChannels
		}
	}
`

// The message's content note (contentNote, A19/A20 follow-up): a plain-text note the Message card shows at its top, such
// as which layout the email goes out in. Its own document, never part of the detail query: an API without the field
// rejects only this one, quietly, and the card shows no note.
export const NOTIFICATION_CONTENT_NOTE_QUERY = gql`
	query notificationContentNote($id: ID!) {
		notificationSetting(id: $id) {
			id
			contentNote
		}
	}
`

// `versionId` previews restoring a history entry, `resetChannel` restoring a channel's default message; with either,
// the server ignores the other change arguments.
export const NOTIFICATION_IMPACT_QUERY = gql`
	query notificationImpact(
		$id: ID!
		$systemEnabled: Boolean
		$channels: [String!]
		$schedule: JSON
		$content: JSON
		$versionId: ID
		$resetChannel: String
	) {
		notificationImpact(
			id: $id
			systemEnabled: $systemEnabled
			channels: $channels
			schedule: $schedule
			content: $content
			versionId: $versionId
			resetChannel: $resetChannel
		) { ${IMPACT_FIELDS} }
	}
`

export const PREVIEW_NOTIFICATION_QUERY = gql`
	query previewNotification($id: ID!, $channel: String!, $content: JSON!) {
		previewNotification(id: $id, channel: $channel, content: $content) {
			subject
			html
			text
			errors
		}
	}
`

// Amendment A15: the preview with one plain-language line per condition and the keys those conditions use, which the
// "Try with" controls offer; sampleOverrides ({ "<key>": "<value>" }) replaces the sample data for that preview only.
// A second document, never merged into the one above: an API without them rejects only this one ("doesn't exist" /
// "doesn't accept argument", not toasted) and the Message card previews with PREVIEW_NOTIFICATION_QUERY from then on.
export const PREVIEW_NOTIFICATION_WITH_CONDITIONS_QUERY = gql`
	query previewNotificationWithConditions($id: ID!, $channel: String!, $content: JSON!, $sampleOverrides: JSON) {
		previewNotification(id: $id, channel: $channel, content: $content, sampleOverrides: $sampleOverrides) {
			subject
			html
			text
			errors
			conditions
			conditionVariables
		}
	}
`

// Amendment A15: which details "+ Add condition" can compare (`conditionable`) and, for a yes/no or enum-like detail,
// the values to pick from with the exact Liquid literal for each. Its own document: an API without the fields rejects
// only this one, quietly, and the dialog offers every detail as free text.
export const NOTIFICATION_VARIABLE_CONDITIONS_QUERY = gql`
	query notificationVariableConditions($id: ID!) {
		notificationSetting(id: $id) {
			id
			variables {
				key
				conditionable
				valueOptions {
					label
					literal
				}
			}
		}
	}
`

// A15 fix round: how each detail is stored (`valueKind`: text | number | boolean | boolean_string | enum) and each
// option's raw `value` (for Try with and display; `literal` stays the Liquid to write). Its own document, never merged
// into NOTIFICATION_VARIABLE_CONDITIONS_QUERY: an API without the fields rejects only this one, quietly, and Add
// condition and Try with work as before (a number is then compared as text).
export const NOTIFICATION_VARIABLE_KINDS_QUERY = gql`
	query notificationVariableKinds($id: ID!) {
		notificationSetting(id: $id) {
			id
			variables {
				key
				valueKind
				valueOptions {
					label
					literal
					value
				}
			}
		}
	}
`

// Amendment A17: which details Draft with AI writes with (`forAi`); the AI panel lists them. Its own document: an API
// without the field rejects only this one, quietly, and the panel lists every detail "+ Insert info" offers.
export const NOTIFICATION_VARIABLES_FOR_AI_QUERY = gql`
	query notificationVariablesForAi($id: ID!) {
		notificationSetting(id: $id) {
			id
			variables {
				key
				forAi
			}
		}
	}
`

export const NOTIFICATION_HISTORY_QUERY = gql`
	query notificationHistory($notificationId: ID, $actorId: ID, $impact: String, $from: ISO8601DateTime, $to: ISO8601DateTime, $first: Int, $after: String) {
		notificationHistory(notificationId: $notificationId, actorId: $actorId, impact: $impact, from: $from, to: $to, first: $first, after: $after) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				id
				at
				actorId
				actorName
				actorRole
				impersonatedBy
				source
				notificationId
				notificationName
				summary
				level
				affectedTotal
				reason
				restorable
				diff {
					field
					label
					before
					after
				}
			}
		}
	}
`

const SETTING_UPDATE_FIELDS = `notificationSetting { ${SETTING_DETAIL_FIELDS} } impact { ${IMPACT_FIELDS} }`

export const UPDATE_NOTIFICATION_SETTING_MUTATION = gql`
	mutation updateNotificationSetting($id: ID!, $systemEnabled: Boolean, $channels: [String!], $schedule: JSON, $content: JSON, $reason: String) {
		updateNotificationSetting(id: $id, systemEnabled: $systemEnabled, channels: $channels, schedule: $schedule, content: $content, reason: $reason) {
			${SETTING_UPDATE_FIELDS}
		}
	}
`

export const RESET_NOTIFICATION_CONTENT_MUTATION = gql`
	mutation resetNotificationContent($id: ID!, $channel: String!, $reason: String) {
		resetNotificationContent(id: $id, channel: $channel, reason: $reason) { ${SETTING_UPDATE_FIELDS} }
	}
`

export const RESTORE_NOTIFICATION_VERSION_MUTATION = gql`
	mutation restoreNotificationVersion($versionId: ID!, $reason: String) {
		restoreNotificationVersion(versionId: $versionId, reason: $reason) { ${SETTING_UPDATE_FIELDS} }
	}
`

export const SEND_NOTIFICATION_TEST_MUTATION = gql`
	mutation sendNotificationTest($id: ID!, $channel: String!, $recipient: String!) {
		sendNotificationTest(id: $id, channel: $channel, recipient: $recipient) {
			ok
			message
		}
	}
`

// Draft with AI in the Message card (amendment A13). The same monthly allowance as Smart Communication; nothing is
// saved by a draft. Own documents: an API without them answers "doesn't exist" and the panel only hides.
export const NOTIFICATION_AI_ASSIST_QUERY = gql`
	query notificationAiAssist {
		notificationAiAssist {
			enabled
			limit
			used
			remaining
			resetsOn
		}
	}
`

// channel is the engine's value ("email" | "sms" | "notification"; subject is the push heading).
export const DRAFT_NOTIFICATION_CONTENT_WITH_AI_MUTATION = gql`
	mutation draftNotificationContentWithAi($id: ID!, $channel: String!, $brief: String, $subject: String, $body: String, $subjectsOnly: Boolean) {
		draftNotificationContentWithAi(id: $id, channel: $channel, brief: $brief, subject: $subject, body: $body, subjectsOnly: $subjectsOnly) {
			subject
			body
			subjects
			remaining
		}
	}
`
